import { useContext, useEffect, useState, type FC } from 'react';
import { useTranslation } from 'react-i18next';

import ConfigContext from 'app/common/ConfigContext';
import BlockGradTemplate from 'app/components/DesignVersion/Grad/Blocks/BlockGradTemplate';
import LoadingContainer from 'app/components/LoadingContainer';
import { getSeriesMatchesFetchUrl } from 'app/utils/entities/series';
import Tabs from 'app/components/Tabs';
import { getMatchType } from 'app/utils/entities/match_data';
import { formatDate, formatTime } from 'app/utils/date';
import TeamAvatar from 'app/components/TeamAvatar';
import ArrowIcon from 'app/icons/arrow-min-right.svg';
import { isDefined } from 'app/utils/types';
import { usePersistentFetch } from 'app/hooks/usePersistentFetch';
import { getXTotal } from 'app/utils/api';
import { ERROR_UNKNOWN } from 'app/constants/errors';

import style from './style.module.scss';

interface Props {
  data: SeriesOverviewBlockMatch;
}

const NB_DIPLAYED_MATCHES = 5;

const SeriesMatchesBlockGrad: FC<Props> = ({ data }) => {
  const { t } = useTranslation();
  const config = useContext(ConfigContext);
  const { lang } = config;

  const [tab, setTab] = useState<'finished' | 'upcoming'>('finished');
  const [page, setPage] = useState(0);
  // TODO: handle max pages
  const [nbMaxPages, setNbMaxPages] = useState(0);

  const changeTab = (newTab: 'finished' | 'upcoming') => {
    setTab(newTab);
    setPage(0);
    setNbMaxPages(1);
  };

  const loadPreviousPage = () => {
    if (page > 0) {
      setPage(page - 1);
    }
  };

  const loadNextPage = () => {
    if (page < nbMaxPages - 1) {
      setPage(page + 1);
    }
  };

  const {
    status,
    data: displayedMatches,
    error,
    headers
  } = usePersistentFetch<SeriesMatch[]>({
    method: 'GET',
    fetchPolicy: 'cache-first',
    url: getSeriesMatchesFetchUrl(data.id, tab, page, NB_DIPLAYED_MATCHES, config),
    preventRequest: !data
  });

  useEffect(() => {
    if (headers) {
      const total = getXTotal(headers);
      const maxPages = Math.ceil(total / NB_DIPLAYED_MATCHES);
      setNbMaxPages(maxPages);
    }
  }, [headers]);

  return (
    <BlockGradTemplate hasData title={t('block-series_matches-title')}>
      <Tabs
        selectTab={changeTab}
        selectedTab={tab}
        tabs={['finished', 'upcoming']}
        compact
        translationKey="block-series_matches-tabs"
        className={style.tabs}
      />
      <LoadingContainer
        data={displayedMatches}
        error={error ? ERROR_UNKNOWN : null}
        status={status}
        firstLoadIndicatorOnly
      >
        <div className={style.matchesHeader}>
          <span>{t('block-series_matches-time')}</span>
          <span>{t('block-series_matches-teams')}</span>
          <span>{t('block-series_matches-date')}</span>
          <span>{t('block-series_matches-format')}</span>
        </div>
        <ul className={style.matchesContent}>
          {displayedMatches?.map(match => (
            <li key={match.id} className={style.match}>
              <div>{match.begin_at ? formatTime(new Date(match.begin_at), lang) : '-'}</div>
              <div className={style.matchTeams}>
                <TeamAvatar team={match.opponents.at(0)} className={style.matchTeam} />
                <div className={style.matchScore}>
                  {match.opponents.some(o => isDefined(o.score)) ? (
                    <>
                      <span>{match.opponents.at(0)?.score ?? '-'}</span>
                      <span>-</span>
                      <span>{match.opponents.at(1)?.score ?? '-'}</span>
                    </>
                  ) : (
                    <span>{t('all-versus')}</span>
                  )}
                </div>
                <TeamAvatar team={match.opponents.at(1)} className={style.matchTeam} />
              </div>
              <div>
                {match.begin_at
                  ? formatDate(new Date(match.begin_at), lang, {
                      day: 'numeric',
                      month: '2-digit',
                      year: 'numeric'
                    })
                  : '-'}
              </div>
              <div>{getMatchType(match)}</div>
            </li>
          ))}
        </ul>
      </LoadingContainer>
      <div className={style.matchesFooter}>
        <button
          type="button"
          onClick={loadPreviousPage}
          className={style.prevButton}
          disabled={page === 0}
        >
          <ArrowIcon className={style.prevArrow} />
        </button>
        {nbMaxPages > 0 && (
          <span>
            {page + 1} / {nbMaxPages}
          </span>
        )}
        <button
          type="button"
          onClick={loadNextPage}
          className={style.nextButton}
          disabled={page >= nbMaxPages - 1}
        >
          <ArrowIcon className={style.nextArrow} />
        </button>
      </div>
    </BlockGradTemplate>
  );
};

SeriesMatchesBlockGrad.displayName = 'SeriesMatchesBlockGrad';

export default SeriesMatchesBlockGrad;
